import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip,
  Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, Tab, Tabs,
  IconButton,
} from '@mui/material';
import {
  CalendarMonth, NightsStay,
  Close, NavigateBefore, NavigateNext,
  Star,
} from '@mui/icons-material';
import EclipsePanel from '../components/space/EclipsePanel';
import AstroEventCard from '../components/space/AstroEventCard';

// ─── Astronomical calculations ────────────────────────────────────────────────
function getMoonPhase(date) {
  const known = new Date(2000, 0, 6);
  const diff  = (date - known) / (1000 * 60 * 60 * 24);
  const cycle = 29.53058867;
  const phase = ((diff % cycle) + cycle) % cycle;
  if (phase < 1.85)  return { name: 'New Moon',        icon: '🌑', value: 0 };
  if (phase < 7.38)  return { name: 'Waxing Crescent', icon: '🌒', value: 1 };
  if (phase < 9.22)  return { name: 'First Quarter',   icon: '🌓', value: 2 };
  if (phase < 14.76) return { name: 'Waxing Gibbous',  icon: '🌔', value: 3 };
  if (phase < 16.61) return { name: 'Full Moon',       icon: '🌕', value: 4 };
  if (phase < 22.15) return { name: 'Waning Gibbous',  icon: '🌖', value: 5 };
  if (phase < 23.99) return { name: 'Last Quarter',    icon: '🌗', value: 6 };
  if (phase < 29.53) return { name: 'Waning Crescent', icon: '🌘', value: 7 };
  return { name: 'New Moon', icon: '🌑', value: 0 };
}

function getNextFullMoons(count = 6) {
  const moons = [];
  let date    = new Date();
  let found   = 0;
  while (found < count) {
    const phase = getMoonPhase(date);
    if (phase.value === 4) {
      if (moons.length === 0 || moons[moons.length - 1].toDateString() !== date.toDateString()) {
        moons.push(new Date(date));
        found++;
      }
    }
    date = new Date(date.getTime() + 1000 * 60 * 60 * 24);
  }
  return moons;
}

function getAstroEvents() {
  const now  = new Date();
  const year = now.getFullYear();
  return [
    { id:1,  type:'meteor',    title:'Perseid Meteor Shower',           date:new Date(year,7,12),  endDate:new Date(year,7,13),  description:'One of the best meteor showers of the year. Up to 100 meteors per hour at peak.',            visibility:'Excellent',            color:'#ffa726', icon:'☄️' },
    { id:2,  type:'meteor',    title:'Geminid Meteor Shower',           date:new Date(year,11,13), endDate:new Date(year,11,14), description:'The strongest meteor shower of the year, producing up to 120 multicolored meteors per hour.',  visibility:'Excellent',            color:'#ffa726', icon:'☄️' },
    { id:3,  type:'meteor',    title:'Leonid Meteor Shower',            date:new Date(year,10,17),                               description:'Associated with comet Tempel-Tuttle. Produces up to 15 meteors per hour.',                   visibility:'Good',                 color:'#ffa726', icon:'☄️' },
    { id:4,  type:'planet',    title:'Mars Opposition',                 date:new Date(year,0,16),                                description:'Mars is at its closest approach to Earth. Best time to view with a telescope.',               visibility:'Excellent',            color:'#ef5350', icon:'🔴' },
    { id:5,  type:'planet',    title:'Jupiter at Opposition',           date:new Date(year,10,3),                                description:'Jupiter at its closest approach. Its disk will be at its largest and brightest.',              visibility:'Excellent',            color:'#c9a96e', icon:'🪐' },
    { id:6,  type:'planet',    title:'Saturn at Opposition',            date:new Date(year,8,21),                                description:'Saturn and its rings are at their most visible.',                                             visibility:'Excellent',            color:'#e4d191', icon:'🪐' },
    { id:7,  type:'alignment', title:'Planetary Alignment — Venus & Jupiter', date:new Date(year,1,28),                         description:'Venus and Jupiter appear very close together in the evening sky.',                            visibility:'Good',                 color:'#ffffff', icon:'✨' },
    { id:8,  type:'alignment', title:'Grand Planetary Parade',          date:new Date(year,5,3),                                 description:'Six planets simultaneously visible in the morning sky.',                                      visibility:'Good',                 color:'#7e57c2', icon:'🌌' },
    { id:9,  type:'eclipse',   title:'Lunar Eclipse',                   date:new Date(year,2,14),                                description:'A total lunar eclipse visible from the Americas and Europe.',                                  visibility:'Americas, Europe',     color:'#ef5350', icon:'🌕', duration:'3h 29m' },
    { id:10, type:'eclipse',   title:'Solar Eclipse — Partial',         date:new Date(year,8,21),                                description:'A partial solar eclipse visible from parts of the Southern Hemisphere.',                      visibility:'Southern Hemisphere',  color:'#ffd600', icon:'🌑' },
    { id:11, type:'solstice',  title:'Summer Solstice',                 date:new Date(year,5,21),                                description:'The longest day of the year in the Northern Hemisphere.',                                    visibility:'Global',               color:'#ffd600', icon:'☀️' },
    { id:12, type:'solstice',  title:'Winter Solstice',                 date:new Date(year,11,21),                               description:'The shortest day of the year in the Northern Hemisphere.',                                   visibility:'Global',               color:'#81d4fa', icon:'❄️' },
    { id:13, type:'solstice',  title:'Vernal Equinox',                  date:new Date(year,2,20),                                description:'Day and night are approximately equal. Spring begins in the Northern Hemisphere.',           visibility:'Global',               color:'#a5d6a7', icon:'🌸' },
    { id:14, type:'solstice',  title:'Autumnal Equinox',                date:new Date(year,8,22),                                description:'Autumn begins in the Northern Hemisphere.',                                                   visibility:'Global',               color:'#ffb74d', icon:'🍂' },
    { id:15, type:'special',   title:'Supermoon',                       date:new Date(year,9,7),                                 description:'Moon at perigee while full. Appears 14% bigger and 30% brighter.',                          visibility:'Global',               color:'#f8bbd0', icon:'🌕' },
    { id:16, type:'meteor',    title:'Quadrantid Meteor Shower',        date:new Date(year,0,4),                                 description:'Produces up to 40 meteors per hour at peak.',                                                visibility:'Northern Hemisphere',  color:'#ffa726', icon:'☄️' },
  ].sort((a, b) => a.date - b.date);
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SpaceCalendarPage() {
  const [tab,           setTab]       = useState(0);
  const [currentMonth,  setMonth]     = useState(new Date());
  const [selectedEvent, setEvent]     = useState(null);
  const [moonPhase,     setMoonPhase] = useState(getMoonPhase(new Date()));
  const [fullMoons,     setFullMoons] = useState([]);
  const [events,        setEvents]    = useState([]);
  const [filter,        setFilter]    = useState('all');

  useEffect(() => {
    setFullMoons(getNextFullMoons(6));
    setEvents(getAstroEvents());
    const timer = setInterval(() => setMoonPhase(getMoonPhase(new Date())), 60000);
    return () => clearInterval(timer);
  }, []);

  const filteredEvents  = filter === 'all' ? events : events.filter(e => e.type === filter);
  const upcomingEvents  = filteredEvents.filter(e => e.date >= new Date(new Date().setHours(0,0,0,0))).slice(0, 12);
  const pastEvents      = filteredEvents.filter(e => e.date < new Date(new Date().setHours(0,0,0,0))).slice(-4).reverse();

  const getDaysInMonth  = (date) => {
    const year  = date.getFullYear();
    const month = date.getMonth();
    const first = new Date(year, month, 1).getDay();
    const days  = new Date(year, month + 1, 0).getDate();
    return { first, days };
  };

  const { first, days } = getDaysInMonth(currentMonth);
  const today           = new Date();

  const getEventsForDay = (day) => {
    const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return events.filter(e =>
      e.date.getFullYear() === d.getFullYear() &&
      e.date.getMonth()    === d.getMonth()    &&
      e.date.getDate()     === d.getDate()
    );
  };

  const isFullMoon = (day) => {
    const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return fullMoons.some(fm =>
      fm.getFullYear() === d.getFullYear() &&
      fm.getMonth()    === d.getMonth()    &&
      fm.getDate()     === d.getDate()
    );
  };

  const FILTER_TYPES = [
    { value:'all',       label:'All Events',    color:'#ffffff' },
    { value:'meteor',    label:'☄️ Meteors',    color:'#ffa726' },
    { value:'planet',    label:'🪐 Planets',    color:'#c9a96e' },
    { value:'eclipse',   label:'🌑 Eclipses',   color:'#ef5350' },
    { value:'alignment', label:'✨ Alignments', color:'#7e57c2' },
    { value:'solstice',  label:'☀️ Solstice',   color:'#ffd600' },
    { value:'special',   label:'🌕 Special',    color:'#f8bbd0' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight:800 }}>🔭 AstroTrack Portal</Typography>
          <Typography variant="body2" sx={{ color:'text.secondary', mt:0.5 }}>
            Astronomical events, eclipses & more
          </Typography>
        </Box>
        <Card sx={{ px:2.5, py:1.5, border:'1px solid rgba(255,255,255,0.2)' }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
            <Typography sx={{ fontSize:28 }}>{moonPhase.icon}</Typography>
            <Box>
              <Typography variant="caption" sx={{ color:'text.secondary', display:'block' }}>Tonight's Moon</Typography>
              <Typography variant="body2" sx={{ fontWeight:700, color:'primary.light' }}>{moonPhase.name}</Typography>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb:3,
        '& .MuiTab-root':{ color:'text.secondary', fontWeight:600 },
        '& .Mui-selected':{ color:'primary.light' },
        '& .MuiTabs-indicator':{ bgcolor:'primary.main' },
      }}>
        <Tab icon={<CalendarMonth />} label="Calendar"    iconPosition="start" />
        <Tab icon={<Star />}          label="Events List" iconPosition="start" />
        <Tab icon={<NightsStay />}    label="Eclipses"    iconPosition="start" />
      </Tabs>

      {/* ── Tab 0: Calendar ──────────────────────────────────────────────── */}
      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent sx={{ p:2.5 }}>
                <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2 }}>
                  <IconButton onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth()-1))} sx={{ color:'primary.main' }}>
                    <NavigateBefore />
                  </IconButton>
                  <Typography variant="h6" sx={{ fontWeight:700 }}>
                    {currentMonth.toLocaleString('default', { month:'long', year:'numeric' })}
                  </Typography>
                  <IconButton onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth()+1))} sx={{ color:'primary.main' }}>
                    <NavigateNext />
                  </IconButton>
                </Box>

                <Grid container sx={{ mb:1 }}>
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                    <Grid item xs key={d} sx={{ textAlign:'center' }}>
                      <Typography variant="caption" sx={{ color:'text.secondary', fontWeight:700, fontSize:11 }}>{d}</Typography>
                    </Grid>
                  ))}
                </Grid>

                <Grid container>
                  {Array(first).fill(null).map((_,i) => (
                    <Grid item xs key={`e-${i}`} sx={{ aspectRatio:'1', p:0.3 }}>
                      <Box sx={{ height:'100%', borderRadius:1 }} />
                    </Grid>
                  ))}
                  {Array(days).fill(null).map((_,i) => {
                    const day       = i + 1;
                    const dayEvents = getEventsForDay(day);
                    const isToday   = today.getDate() === day && today.getMonth() === currentMonth.getMonth() && today.getFullYear() === currentMonth.getFullYear();
                    const hasMoon   = isFullMoon(day);
                    return (
                      <Grid item xs key={day} sx={{ aspectRatio:'1', p:0.3 }}>
                        <Box onClick={() => dayEvents.length > 0 && setEvent(dayEvents[0])} sx={{
                          height:'100%', borderRadius:1.5, p:0.5,
                          display:'flex', flexDirection:'column', alignItems:'center',
                          position:'relative', minHeight:48,
                          bgcolor: isToday ? 'rgba(255,255,255,0.15)' : 'transparent',
                          border: isToday ? '1px solid rgba(255,255,255,0.4)' : '1px solid transparent',
                          cursor: dayEvents.length > 0 ? 'pointer' : 'default',
                          '&:hover': dayEvents.length > 0 ? { bgcolor:'rgba(255,255,255,0.08)' } : {},
                          transition:'all 0.15s',
                        }}>
                          <Typography variant="caption" sx={{ fontWeight:isToday?800:400, color:isToday?'primary.light':'text.secondary', fontSize:12 }}>
                            {day}
                          </Typography>
                          {hasMoon && <Typography sx={{ fontSize:14, lineHeight:1 }}>🌕</Typography>}
                          {dayEvents.slice(0,2).map((ev,idx) => (
                            <Box key={idx} sx={{ width:6, height:6, borderRadius:'50%', bgcolor:ev.color, mt:0.3 }} />
                          ))}
                          {dayEvents.length > 2 && (
                            <Typography sx={{ fontSize:9, color:'text.secondary' }}>+{dayEvents.length-2}</Typography>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>

                <Box sx={{ display:'flex', gap:2, mt:2, flexWrap:'wrap' }}>
                  {FILTER_TYPES.slice(1).map(f => (
                    <Box key={f.value} sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                      <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor:f.color }} />
                      <Typography variant="caption" sx={{ color:'text.secondary', fontSize:10 }}>{f.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" sx={{ fontWeight:700, mb:1.5 }}>Next Full Moons 🌕</Typography>
            <Card sx={{ mb:2 }}>
              <CardContent sx={{ p:2 }}>
                {fullMoons.map((fm,i) => (
                  <Box key={i} sx={{ display:'flex', justifyContent:'space-between', py:0.8,
                    borderBottom: i < fullMoons.length-1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                    <Typography variant="body2" sx={{ color:'text.secondary' }}>🌕 Full Moon</Typography>
                    <Typography variant="body2" sx={{ fontWeight:600, color:'text.primary' }}>
                      {fm.toLocaleDateString('en-US', { month:'short', day:'numeric' })}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>

            <Typography variant="subtitle1" sx={{ fontWeight:700, mb:1.5 }}>Coming Up Next</Typography>
            {events.filter(e => e.date >= today).slice(0,4).map(ev => (
              <Card key={ev.id} onClick={() => setEvent(ev)} sx={{
                mb:1, cursor:'pointer', border:`1px solid ${ev.color}33`,
                '&:hover':{ border:`1px solid ${ev.color}88` }, transition:'all 0.2s',
              }}>
                <CardContent sx={{ p:1.5, '&:last-child':{ pb:1.5 } }}>
                  <Box sx={{ display:'flex', gap:1.5, alignItems:'flex-start' }}>
                    <Typography sx={{ fontSize:22 }}>{ev.icon}</Typography>
                    <Box sx={{ flex:1 }}>
                      <Typography variant="body2" sx={{ fontWeight:700, color:ev.color }}>{ev.title}</Typography>
                      <Typography variant="caption" sx={{ color:'text.secondary' }}>
                        {ev.date.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Grid>
        </Grid>
      )}

      {/* ── Tab 1: Events List ────────────────────────────────────────────── */}
      {tab === 1 && (
        <Box>
          <Box sx={{ display:'flex', gap:1, mb:3, flexWrap:'wrap' }}>
            {FILTER_TYPES.map(f => (
              <Chip key={f.value} label={f.label} onClick={() => setFilter(f.value)} sx={{
                fontWeight:700, cursor:'pointer',
                bgcolor: filter===f.value ? `${f.color}33` : 'transparent',
                color:   filter===f.value ? f.color : 'text.secondary',
                border:  filter===f.value ? `1px solid ${f.color}` : '1px solid rgba(255,255,255,0.1)',
                '&:hover':{ bgcolor:`${f.color}22` },
              }} />
            ))}
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight:700, mb:2, color:'primary.light' }}>
            Upcoming Events ({upcomingEvents.length})
          </Typography>
          <Grid container spacing={2} sx={{ mb:3 }}>
            {upcomingEvents.map(ev => (
              <Grid item xs={12} sm={6} md={4} key={ev.id}>
                <AstroEventCard event={ev} onClick={() => setEvent(ev)} />
              </Grid>
            ))}
          </Grid>

          {pastEvents.length > 0 && (
            <>
              <Divider sx={{ borderColor:'rgba(255,255,255,0.1)', my:2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight:700, mb:2, color:'text.secondary' }}>
                Recent Past Events
              </Typography>
              <Grid container spacing={2}>
                {pastEvents.map(ev => (
                  <Grid item xs={12} sm={6} md={4} key={ev.id}>
                    <AstroEventCard event={ev} onClick={() => setEvent(ev)} past />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>
      )}

      {/* ── Tab 2: Eclipses ───────────────────────────────────────────────── */}
      {tab === 2 && <EclipsePanel events={events.filter(e => e.type === 'eclipse')} />}

      {/* ── Event Detail Dialog ───────────────────────────────────────────── */}
      <Dialog open={!!selectedEvent} onClose={() => setEvent(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx:{ bgcolor:'background.paper', border:`1px solid ${selectedEvent?.color || '#ffffff'}44` } }}>
        {selectedEvent && (
          <>
            <DialogTitle sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                <Typography sx={{ fontSize:32 }}>{selectedEvent.icon}</Typography>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight:800, color:selectedEvent.color }}>{selectedEvent.title}</Typography>
                  <Chip label={selectedEvent.type.toUpperCase()} size="small"
                    sx={{ bgcolor:`${selectedEvent.color}22`, color:selectedEvent.color,
                      fontSize:10, fontWeight:700, border:`1px solid ${selectedEvent.color}44` }} />
                </Box>
              </Box>
              <IconButton onClick={() => setEvent(null)} sx={{ color:'text.secondary' }}><Close /></IconButton>
            </DialogTitle>
            <Divider sx={{ borderColor:'rgba(255,255,255,0.1)' }} />
            <DialogContent sx={{ pt:2.5 }}>
              <Typography variant="body2" sx={{ color:'text.secondary', lineHeight:1.9, mb:2 }}>
                {selectedEvent.description}
              </Typography>
              {[
                { label:'Date',       value: selectedEvent.date.toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' }) },
                { label:'Visibility', value: selectedEvent.visibility },
                { label:'Duration',   value: selectedEvent.duration },
              ].map(({ label, value }) => value && (
                <Box key={label} sx={{ display:'flex', justifyContent:'space-between',
                  py:1, borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
                  <Typography variant="caption" sx={{ color:'text.secondary', fontWeight:600 }}>{label}</Typography>
                  <Typography variant="caption" sx={{ color:'text.primary', fontWeight:700 }}>{value}</Typography>
                </Box>
              ))}
              {selectedEvent.date > new Date() ? (
                <Box sx={{ mt:2, p:1.5, borderRadius:2, bgcolor:'rgba(102,187,106,0.1)', border:'1px solid rgba(102,187,106,0.3)' }}>
                  <Typography variant="body2" sx={{ color:'#66bb6a', fontWeight:600 }}>
                    ⏰ In {Math.ceil((selectedEvent.date - new Date()) / (1000*60*60*24))} days
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ mt:2, p:1.5, borderRadius:2, bgcolor:'rgba(84,84,84,0.1)', border:'1px solid rgba(84,84,84,0.3)' }}>
                  <Typography variant="body2" sx={{ color:'text.secondary' }}>This event has already passed</Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p:2 }}>
              <Button onClick={() => setEvent(null)} sx={{ color:'text.secondary' }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}